import { useCallback, useEffect, useMemo, useState } from 'react';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import Alert from 'react-bootstrap/Alert';
import AdminLayout from '../templates/AdminLayout';
import PageHeader from '../molecules/PageHeader';
import ImageDropzone from '../molecules/ImageDropzone';
import BusinessHoursForm, { buildHours, sameBusinessHours } from '../organisms/BusinessHoursForm';
import DeliveryRulesForm, { buildRules, sameRules, rulesError, rulesToPayload } from '../organisms/DeliveryRulesForm';
import PixPaymentForm, { buildPix, samePix, pixError, pixToPayload } from '../organisms/PixPaymentForm';
import BrandColorForm, { buildBrand, sameBrand, brandError, brandToPayload } from '../organisms/BrandColorForm';
import PaymentMethodsForm, { buildPayments, samePayments, paymentsToPayload } from '../organisms/PaymentMethodsForm';
import AppInput from '../atoms/AppInput';
import AppButton from '../atoms/AppButton';
import { IconCheckCircle, IconLink, IconLogOut } from '../atoms/Icon';
import { useNavigate } from 'react-router-dom';
import { uploadEstablishmentLogo, uploadEstablishmentBanner } from '../api/uploadApi';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { formatWhatsapp } from '../utils/phone';

const onlyDigits = (value) => (value || '').replace(/\D/g, '');

// A base do domínio do cardápio é configurável via .env; o backend só nos dá a
// URL completa, então extraímos o slug (último segmento) e recombinamos.
//
// Desde a migração do cardápio para Next.js, cada estabelecimento é um
// subdomínio (`{slug}.mypedidos.com.br`), não mais uma rota de path
// (`mypedidos.com.br/{slug}`) — o middleware do cardápio resolve o
// estabelecimento pelo `Host`. Mesma transformação que o backend já faz em
// `link_preview.py` (`_establishment_base_url`), para o link mostrado aqui
// bater com o que o cardápio e o WhatsApp realmente resolvem.
const MENU_BASE_URL = process.env.REACT_APP_MENU_BASE_URL;

function menuUrlFrom(establishment) {
  if (!establishment?.url) return establishment?.url;
  const slug = establishment.url.split('/').filter(Boolean).pop();
  if (!MENU_BASE_URL) return establishment.url;
  const base = new URL(MENU_BASE_URL);
  // Antes da migração, o cardápio vivia num subdomínio fixo
  // ("cardapio.mypedidos.com.br") com o estabelecimento como path. Se a
  // env var ainda carregar esse valor antigo, remove o prefixo antes de
  // encaixar o slug — sem isso o link sairia "hogwarts.cardapio.mypedidos.com.br"
  // em vez de "hogwarts.mypedidos.com.br".
  const host = base.host.replace(/^cardapio\./, '');
  return `${base.protocol}//${slug}.${host}`;
}

/** Estado do formulário a partir do estabelecimento salvo — serve tanto para
 *  inicializar quanto para descartar alterações. */
function formStateFrom(establishment) {
  return {
    name: establishment?.name || '',
    email: establishment?.email || '',
    whatsappNumber: formatWhatsapp(establishment?.whatsappNumber || ''),
    logoUrl: establishment?.logoUrl || '',
    bannerUrl: establishment?.bannerUrl || '',
    instagramUrl: establishment?.instagramUrl || '',
    hours: buildHours(establishment?.businessHours),
    rules: buildRules(establishment),
    pix: buildPix(establishment),
    brand: buildBrand(establishment),
    payments: buildPayments(establishment),
  };
}

function SettingsPage() {
  const { establishment, updateEstablishment, logout } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  const [form, setForm] = useState(() => formStateFrom(establishment));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const saved = useMemo(() => formStateFrom(establishment), [establishment]);

  // Salvar atualiza o estabelecimento no contexto, o que rebaseia a comparação
  // abaixo e faz a barra de ações desaparecer sozinha.
  const isDirty =
    form.name.trim() !== saved.name ||
    form.email.trim() !== saved.email ||
    onlyDigits(form.whatsappNumber) !== onlyDigits(saved.whatsappNumber) ||
    form.logoUrl !== saved.logoUrl ||
    form.bannerUrl !== saved.bannerUrl ||
    form.instagramUrl.trim() !== saved.instagramUrl ||
    !sameBusinessHours(form.hours, saved.hours) ||
    !sameRules(form.rules, saved.rules) ||
    !samePix(form.pix, saved.pix) ||
    !sameBrand(form.brand, saved.brand) ||
    !samePayments(form.payments, saved.payments);

  // O usuário perde o que digitou se sair da tela sem salvar; o aviso do
  // navegador é o único ponto onde ainda dá para voltar atrás.
  useEffect(() => {
    if (!isDirty) return;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [isDirty]);

  const patch = useCallback((changes) => {
    setForm((prev) => ({ ...prev, ...changes }));
    setError(null);
  }, []);

  function handleLogout() {
    logout();
    navigate('/entrar');
  }

  function handleDiscard() {
    setForm(formStateFrom(establishment));
    setError(null);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.whatsappNumber.trim()) {
      setError('Preencha nome, e-mail e WhatsApp.');
      return;
    }
    const problemaNasRegras = rulesError(form.rules) || pixError(form.pix) || brandError(form.brand);
    if (problemaNasRegras) {
      setError(problemaNasRegras);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await updateEstablishment({
        name: form.name.trim(),
        email: form.email.trim(),
        whatsappNumber: form.whatsappNumber.trim(),
        logoUrl: form.logoUrl.trim(),
        bannerUrl: form.bannerUrl.trim(),
        instagramUrl: form.instagramUrl.trim(),
        businessHours: form.hours,
        ...rulesToPayload(form.rules),
        ...pixToPayload(form.pix),
        ...brandToPayload(form.brand),
        ...paymentsToPayload(form.payments),
      });
      toast.success('Configurações salvas com sucesso!');
    } catch (e2) {
      setError(e2.message);
      toast.error(e2.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleCopyLink() {
    try {
      await navigator.clipboard.writeText(menuUrlFrom(establishment));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Não foi possível copiar o link.');
    }
  }

  return (
    <AdminLayout>
      <div className="settings-page">
      <PageHeader title="Configurações" />

      {/* Hero: identidade do estabelecimento */}
      <Card
        className="border-0 card-elevated mb-4"
        style={{
          borderRadius: 'var(--radius-md)',
          background: 'linear-gradient(120deg, var(--color-purple-ghost) 0%, #fff 70%)',
        }}
      >
        <Card.Body className="px-4 py-3 d-flex align-items-center gap-3 flex-wrap">
          <ImageDropzone
            value={form.logoUrl}
            onChange={(logoUrl) => patch({ logoUrl })}
            uploadFn={uploadEstablishmentLogo}
            shape="circle"
            size={72}
            actionsLayout="inline"
            hint="Logo do estabelecimento · até 5MB"
          />
          <div style={{ minWidth: 180 }}>
            <h5 className="fw-bold mb-1" style={{ color: 'var(--color-neutral)' }}>
              {establishment?.name || 'Meu estabelecimento'}
            </h5>
            <div style={{ fontSize: 13, color: '#777' }}>{establishment?.email}</div>
          </div>
        </Card.Body>
      </Card>

      {/* Banner: a imagem/vídeo de topo do cardápio, separado da logo porque é
          um formato diferente (retangular, ~3:1) e pode ser vídeo — a logo
          nunca é. */}
      <Card className="border-0 card-elevated mb-4" style={{ borderRadius: 'var(--radius-md)' }}>
        <Card.Body className="p-4">
          <h6 className="fw-semibold mb-1" style={{ color: 'var(--color-neutral)' }}>
            Banner do cardápio
          </h6>
          <p className="mb-3" style={{ fontSize: 12, color: '#888' }}>
            Aparece no topo do cardápio online. Pode ser imagem, GIF ou vídeo curto.
          </p>
          <ImageDropzone
            value={form.bannerUrl}
            onChange={(bannerUrl) => patch({ bannerUrl })}
            uploadFn={uploadEstablishmentBanner}
            shape="wide"
            size={72}
            actionsLayout="inline"
          />
        </Card.Body>
      </Card>

      {error && (
        <Alert variant="danger" className="py-2 px-3 mb-3" style={{ fontSize: 13, borderRadius: 8 }}>
          {error}
        </Alert>
      )}

      {/* Coluna principal com os formulários, coluna estreita à direita com os
          cartões só de leitura. */}
      <div className="settings-grid">
        <div className="d-flex flex-column gap-4">
        {/* Dados do estabelecimento */}
        <Card className="border-0 card-elevated" style={{ borderRadius: 'var(--radius-md)' }}>
          <Card.Body className="p-4">
            <h6 className="fw-semibold mb-3" style={{ color: 'var(--color-neutral)' }}>
              Dados do estabelecimento
            </h6>

            {/* O id permite submeter pelo botão da barra de ações, que vive fora
                do <form>; Enter dentro dos campos também salva. */}
            <Form onSubmit={handleSubmit} id="settings-form">
              <AppInput
                label="Nome do estabelecimento"
                value={form.name}
                onChange={(e) => patch({ name: e.target.value })}
                placeholder="Ex: Restaurante da Maria"
                autoComplete="organization"
                autoCapitalize="words"
                autoCorrect="off"
                enterKeyHint="next"
                required
              />
              <div className="d-flex gap-3 flex-wrap">
                <div className="flex-grow-1" style={{ minWidth: 220 }}>
                  <AppInput
                    label="E-mail"
                    type="email"
                    value={form.email}
                    onChange={(e) => patch({ email: e.target.value })}
                    autoComplete="email"
                    inputMode="email"
                    autoCapitalize="off"
                    autoCorrect="off"
                    spellCheck="false"
                    enterKeyHint="next"
                    required
                  />
                </div>
                <div className="flex-grow-1" style={{ minWidth: 220 }}>
                  <AppInput
                    label="WhatsApp"
                    type="tel"
                    value={form.whatsappNumber}
                    onChange={(e) => patch({ whatsappNumber: formatWhatsapp(e.target.value) })}
                    placeholder="(11) 99999-9999"
                    hint="Apenas números, com DDD."
                    autoComplete="tel-national"
                    inputMode="numeric"
                    enterKeyHint="done"
                    required
                  />
                </div>
              </div>
              <AppInput
                label="Instagram"
                type="url"
                value={form.instagramUrl}
                onChange={(e) => patch({ instagramUrl: e.target.value })}
                placeholder="https://instagram.com/seu_estabelecimento"
                hint="Link do perfil — aparece no modal de informações do cardápio."
                autoComplete="off"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck="false"
                enterKeyHint="done"
              />
            </Form>
          </Card.Body>
        </Card>

        {/* Formas de atendimento antes do horário: primeiro se define o que o
            estabelecimento faz, depois quando ele faz. O horário também é o
            cartão mais alto — sete linhas —, e acima ele empurrava as regras
            para fora da primeira tela. */}
        <DeliveryRulesForm value={form.rules} onChange={(rules) => patch({ rules })} />

        {/* Depois das regras de atendimento: primeiro se define como o pedido
            chega ao cliente, depois como o dinheiro chega ao lojista. */}
        {/* Antes do cartão de Pix: primeiro o lojista diz COMO aceita receber,
            depois preenche os dados de quem recebe. Na ordem inversa, o campo
            de chave aparecia antes de existir motivo para ele. */}
        <PaymentMethodsForm
          value={form.payments}
          onChange={(payments) => patch({ payments })}
          temChavePix={!!form.pix.key.trim()}
        />

        <PixPaymentForm
          value={form.pix}
          onChange={(pix) => patch({ pix })}
          nomeDoEstabelecimento={form.name.trim()}
        />

        {/* A cor vem depois do dinheiro e antes do horário: é a única seção
            desta coluna que muda o que o CLIENTE vê, e não como o pedido é
            atendido. Junto das regras de operação ela pareceria configuração
            de sistema, quando é decisão de marca. */}
        <BrandColorForm value={form.brand} onChange={(brand) => patch({ brand })} />

        <BusinessHoursForm value={form.hours} onChange={(hours) => patch({ hours })} />
        </div>

        <div className="d-flex flex-column gap-4">
          {/* Link público do cardápio */}
          <Card className="border-0 card-elevated" style={{ borderRadius: 'var(--radius-md)' }}>
            <Card.Body className="p-4">
              <div className="d-flex align-items-center gap-2 mb-1">
                <IconLink size={16} style={{ color: 'var(--color-purple-primary)' }} />
                <h6 className="fw-semibold mb-0" style={{ color: 'var(--color-neutral)' }}>
                  Link público do cardápio
                </h6>
              </div>
              <p className="mb-3" style={{ fontSize: 12, color: '#888' }}>
                Compartilhe esse link com seus clientes para que vejam o cardápio online.
              </p>
              <div
                className="mb-2"
                style={{
                  fontSize: 13,
                  color: '#666',
                  backgroundColor: '#fafafa',
                  borderRadius: 8,
                  padding: '9px 14px',
                  overflowWrap: 'anywhere',
                }}
              >
                {menuUrlFrom(establishment)}
              </div>
              <AppButton variant="ghost" onClick={handleCopyLink} className="w-100">
                {copied ? (
                  <span className="d-flex align-items-center justify-content-center gap-1">
                    <IconCheckCircle size={15} /> Copiado!
                  </span>
                ) : (
                  'Copiar link'
                )}
              </AppButton>
            </Card.Body>
          </Card>
        </div>
      </div>

      {/* Sair — só no mobile, já que o header (onde ficava) some nesse tamanho */}
      <button
        type="button"
        onClick={handleLogout}
        className="d-md-none w-100 d-flex align-items-center justify-content-center gap-2 mt-4"
        style={{
          borderRadius: 'var(--radius-md)',
          border: '1px solid #fdecea',
          backgroundColor: '#fdecea',
          color: '#e74c3c',
          padding: '12px 0',
          fontSize: 14,
          fontWeight: 600,
        }}
      >
        <IconLogOut size={16} />
        Sair
      </button>

      {/* Barra de ações — um único salvamento para a tela inteira (dados, logo e
          horários), visível só quando há o que salvar. Antes eram dois botões em
          rodapés separados, e o do horário ficava abaixo da dobra. */}
      {isDirty && (
        <div className="settings-savebar">
          <span className="settings-savebar-text">
            <span className="settings-savebar-dot" />
            Você tem alterações não salvas
          </span>
          <AppButton variant="ghost" onClick={handleDiscard} disabled={saving}>
            Descartar
          </AppButton>
          <AppButton type="submit" form="settings-form" variant="primary" disabled={saving}>
            {saving ? 'Salvando...' : 'Salvar alterações'}
          </AppButton>
        </div>
      )}
      </div>
    </AdminLayout>
  );
}

export default SettingsPage;
